/**
 * The API client.
 *
 * Money never becomes a JS number here. The server sends both `*_cents`
 * (a string, because it is a BIGINT) and a formatted decimal string; the
 * client only ever displays the latter and compares the former as strings or
 * BigInt. Nothing in the browser does money arithmetic.
 */
import { apiFailure } from './lib/apiError.js';

const TOKEN_KEY = 'accounting.token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(message, status, code, payload) {
    super(message);
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

async function request(method, path, body) {
  const token = getToken();
  const res = await fetch(path, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    if (res.status === 401 && token) clearToken();
    const { message, code } = apiFailure(payload, res.status);
    throw new ApiError(message, res.status, code, payload);
  }
  return payload;
}

export const api = {
  get:   (p) => request('GET', p),
  post:  (p, b) => request('POST', p, b),
  put:   (p, b) => request('PUT', p, b),
  patch: (p, b) => request('PATCH', p, b),
  del:   (p) => request('DELETE', p),
};

/**
 * Open an authenticated URL in a new tab.
 *
 * A plain <a href> cannot carry the bearer token, so the response is fetched,
 * turned into a blob URL and opened. The error path matters as much as the
 * happy one here: the PDF endpoint answers 422 with JSON when a tax invoice is
 * not yet s.20 compliant, and that has to surface as a message rather than a
 * broken download.
 */
export async function openAuthed(path) {
  const res = await fetch(path, {
    headers: { authorization: `Bearer ${getToken()}` },
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    const { message, code } = apiFailure(payload, res.status, 'could not open');
    throw new ApiError(message, res.status, code, payload);
  }

  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  // Give the new tab time to claim it before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Format a decimal string for display: "1234.56" -> "1 234.56", negatives in brackets. */
export function money(value) {
  if (value === null || value === undefined || value === '') return '—';
  const s = String(value);
  const neg = s.startsWith('-');
  const [whole, frac = '00'] = (neg ? s.slice(1) : s).split('.');
  // Thin space grouping, which is the South African convention and reads more
  // cleanly in a column than a comma.
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return neg ? `(${grouped}.${frac})` : `${grouped}.${frac}`;
}

/** True when a formatted amount is zero, whatever its sign or spacing. */
export const isZero = (value) => /^-?0\.00$/.test(String(value ?? '0.00'));

/**
 * The default reporting period for an entity.
 *
 * Prefers the financial year the SERVER says is current. Inferring it from the
 * year-end month alone produced a range the entity had no financial year for,
 * which is a period nothing can be reported on — the inference is kept only as
 * a fallback for an entity that has no years defined at all.
 */
export function entityRange(entity) {
  if (entity?.financial_year_start && entity?.financial_year_end) {
    return { from: entity.financial_year_start, to: entity.financial_year_end };
  }
  return defaultRange(entity?.financial_year_end_month ?? 2);
}

/** Fallback only: guess a financial year from its end month. */
export function defaultRange(fyEndMonth = 2) {
  const now = new Date();
  const y = now.getUTCFullYear();
  // The current financial year ends in fyEndMonth; if we are past that month,
  // the year end is next calendar year.
  const endYear = now.getUTCMonth() + 1 > fyEndMonth ? y + 1 : y;
  const startMonth = fyEndMonth === 12 ? 1 : fyEndMonth + 1;
  const startYear = fyEndMonth === 12 ? endYear : endYear - 1;
  const iso = (d) => d.toISOString().slice(0, 10);
  return {
    from: iso(new Date(Date.UTC(startYear, startMonth - 1, 1))),
    to: iso(new Date(Date.UTC(endYear, fyEndMonth, 0))),
  };
}
